import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FolderIcon from "@mui/icons-material/Folder";
import AddIcon from "@mui/icons-material/Add";
import CategoryIcon from "@mui/icons-material/Category";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { Category } from "./types/categoryTypes";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import AddSubcategoryModal from "./AddSubcategoryModal";
import EditSubcategoryModal from "./EditSubcategoryModal";
import DeleteSubcategoryModal from "./DeleteSubcategoryModal";

// ==================== INTERFACES ====================
interface ViewCategoryDetailProps {
  category: Category;
  onBack: () => void;
  onViewSubcategory: (cat: Category) => void;
  onEdit: (cat: Category) => void;
  onDelete: (cat: Category) => void;
  onRefetchCategoryList?: () => void | Promise<void>;
}

// ==================== COMPONENT ====================
const ViewCategoryDetail: React.FC<ViewCategoryDetailProps> = ({
  category,
  onBack,
  onViewSubcategory,
  onEdit,
  onDelete,
  onRefetchCategoryList
}) => {
  // ==================== HOOKS ====================
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { categoryList } = useSelector((state: RootState) => state.categories);
  const { t } = useKivunovaTranslation();

  // ==================== STATE ====================
  const [openAddSubcategoryModal, setOpenAddSubcategoryModal] = useState(false);
  const [openEditSubcategoryModal, setOpenEditSubcategoryModal] =
    useState(false);
  const [openDeleteSubcategoryModal, setOpenDeleteSubcategoryModal] =
    useState(false);
  const [selectedSubcategory, setSelectedSubcategory] =
    useState<Category | null>(null);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  // ==================== EFFECTS ====================
  useEffect(() => {
    const fetchSubcategories = async () => {
      setLoading(true);
      try {
        const subs = categoryList.filter((cat) => cat.parentId === category.id);
        setSubcategories(subs);
      } catch (error) {
        console.error("Failed to fetch subcategories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubcategories();
  }, [category.id, categoryList]);

  // ==================== HANDLERS ====================
  const handleEditSubcategory = (subcategory: Category) => {
    setSelectedSubcategory(subcategory);
    setOpenEditSubcategoryModal(true);
  };

  const handleDeleteSubcategory = (subcategory: Category) => {
    setSelectedSubcategory(subcategory);
    setOpenDeleteSubcategoryModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditSubcategoryModal(false);
    setSelectedSubcategory(null);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteSubcategoryModal(false);
    setSelectedSubcategory(null);
  };

  // ==================== COMPUTED VALUES ====================
  const totalProducts = category.productCount || 0;
  const directProducts = category.directProductCount || 0;
  const inSubcategoryProducts = Math.max(totalProducts - directProducts, 0);

  // ==================== RENDER ====================
  return (
    <Box sx={{ minHeight: "calc(100vh - 110px)", mt: 1 }}>
      {/* ========== HEADER SECTION ========== */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton
          onClick={onBack}
          sx={{
            color: BRAND_COLOR,
            "&:hover": { backgroundColor: `${BRAND_COLOR}15` }
          }}
          aria-label={t("go-back-to-category-list", "Go back to category list")}
        >
          <ArrowBackIcon />
        </IconButton>

        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <CategoryIcon color="primary" />
            <Typography variant="h5" fontWeight="bold">
              {category.name}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t(
              "category-details-subcategories",
              "Category Details & Subcategories"
            )}
          </Typography>
        </Box>

        <Stack direction={matchMD ? "column" : "row"} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            onClick={() => onEdit(category)}
            sx={{
              textTransform: "none",
              color: BRAND_COLOR,
              borderColor: BRAND_COLOR,
              "&:hover": {
                borderColor: BRAND_HOVER,
                backgroundColor: `${BRAND_COLOR}10`
              }
            }}
          >
            {t("edit-category", "Edit Category")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={() => onDelete(category)}
            sx={{ textTransform: "none" }}
          >
            {t("delete", "Delete")}
          </Button>
        </Stack>
      </Box>

      {/* ========== CATEGORY INFORMATION CARD ========== */}
      <Card sx={{ mb: 3, boxShadow: theme.shadows[2] }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2} color="primary">
            {t("category-information", "Category Information")}
          </Typography>
          <Grid container spacing={3}>
            {/* Description */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
                fontWeight={600}
              >
                {t("description", "Description")}
              </Typography>
              <Typography variant="body1">
                {category.description ||
                  t("no-description-provided", "No description provided")}
              </Typography>
            </Grid>

            {/* Status */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
                fontWeight={600}
              >
                {t("status", "Status")}
              </Typography>
              <Chip
                label={category.status || "ACTIVE"}
                color={
                  category.status?.toLowerCase() === "active"
                    ? "success"
                    : "default"
                }
                size="small"
                sx={{ fontWeight: 500 }}
              />
            </Grid>

            {/* Total Products */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
                fontWeight={600}
              >
                {t("total-products", "Total Products")}
              </Typography>
              <Typography variant="h6" color="primary" fontWeight={700}>
                {totalProducts}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("direct", "Direct")}: {directProducts} |{" "}
                {t("in-subcategories", "In Subcategories")}:{" "}
                {inSubcategoryProducts}
              </Typography>
            </Grid>

            {/* Category Path */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
                fontWeight={600}
              >
                {t("category-path", "Category Path")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  color: "text.primary",
                  backgroundColor: "grey.100",
                  p: 1,
                  borderRadius: 1
                }}
              >
                {category.path || `/${category.name.toLowerCase()}`}
              </Typography>
            </Grid>

            {/* Depth Level */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
                fontWeight={600}
              >
                {t("depth-level", "Depth Level")}
              </Typography>
              <Chip
                label={`${t("level", "Level")} ${category.depth || 0}`}
                size="small"
                variant="outlined"
                color="primary"
              />
            </Grid>

            {/* Subcategories Count */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
                fontWeight={600}
              >
                {t("subcategories", "Subcategories")}
              </Typography>
              <Typography variant="h6" color="primary" fontWeight={700}>
                {subcategories.length}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ========== SUBCATEGORIES SECTION ========== */}
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          flexWrap={matchMD ? "wrap" : "nowrap"}
          gap={2}
        >
          <Typography variant="h6" fontWeight={600}>
            {t("subcategories-count", "Subcategories")} ({subcategories.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddSubcategoryModal(true)}
            sx={{
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER },
              textTransform: "none"
            }}
          >
            {t("add-subcategory", "Add Subcategory")}
          </Button>
        </Box>

        {/* Loading State */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress sx={{ color: BRAND_COLOR }} />
          </Box>
        ) : subcategories.length === 0 ? (
          <Alert severity="info">
            {t(
              "no-subcategories-found",
              'No subcategories found. Click "Add Subcategory" to create one.'
            )}
          </Alert>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ boxShadow: theme.shadows[1] }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                    {t("subcategory-name", "Subcategory Name")}
                  </TableCell>
                  {!matchMD && (
                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                      {t("description", "Description")}
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                    {t("products", "Products")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                    {t("status", "Status")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 600, fontSize: 13 }}
                  >
                    {t("actions", "Actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subcategories.map((sub) => (
                  <TableRow key={sub.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <FolderIcon color="primary" fontSize="small" />
                        <Box>
                          <Typography fontWeight={500} fontSize={14}>
                            {sub.name}
                          </Typography>
                          {matchMD && sub.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {sub.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {!matchMD && (
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {sub.description || "-"}
                        </Typography>
                      </TableCell>
                    )}

                    <TableCell>
                      <Chip
                        label={sub.productCount || 0}
                        size="small"
                        color={
                          (sub.productCount || 0) > 0 ? "primary" : "default"
                        }
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={sub.status || "ACTIVE"}
                        color={
                          sub.status?.toLowerCase() === "active"
                            ? "success"
                            : "default"
                        }
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <IconButton
                          size="small"
                          onClick={() => onViewSubcategory(sub)}
                          sx={{
                            color: BRAND_COLOR,
                            "&:hover": { backgroundColor: `${BRAND_COLOR}15` }
                          }}
                          title={t("view-details", "View Details")}
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditSubcategory(sub)}
                          color="primary"
                          title={t("edit-subcategory", "Edit Subcategory")}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSubcategory(sub)}
                          color="error"
                          title={t("delete-subcategory", "Delete Subcategory")}
                        >
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ========== MODALS ========== */}
      <AddSubcategoryModal
        open={openAddSubcategoryModal}
        onClose={() => setOpenAddSubcategoryModal(false)}
        parentCategory={{
          id: category.id,
          name: category.name,
          path: category.path,
          depth: category.depth
        }}
      />

      {selectedSubcategory && (
        <EditSubcategoryModal
          open={openEditSubcategoryModal}
          onClose={handleCloseEditModal}
          subcategory={selectedSubcategory}
          parentCategory={category}
          onSuccess={onRefetchCategoryList}
        />
      )}

      {selectedSubcategory && (
        <DeleteSubcategoryModal
          open={openDeleteSubcategoryModal}
          onClose={handleCloseDeleteModal}
          subcategory={selectedSubcategory}
          parentCategory={category}
        />
      )}
    </Box>
  );
};

export default ViewCategoryDetail;
